import { useEffect, useMemo, useRef, useState } from "react";
import { useSession } from "@/auth/session";
import {
  createInstallmentAgreement,
  listInstallmentsByContract,
  updateInstallmentStatus,
  type InstallmentItem,
} from "@/lib/api";

interface Props {
  contractId: string;
  contractNumber: string;
  deudaTotal?: number;
  onClose: () => void;
  onChanged?: () => void;
}

type View = "list" | "new";

const STATUS_LABEL: Record<string, string> = {
  active: "Activo",
  completed: "Completado",
  cancelled: "Anulado",
  paused: "Pausado",
};

const STATUS_CLASS: Record<string, string> = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  completed: "bg-blue-50 text-blue-700 border-blue-200",
  cancelled: "bg-gray-100 text-gray-500 border-gray-200",
  paused: "bg-amber-50 text-amber-700 border-amber-200",
};

function currentPeriod(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

export default function ConvenioModal({ contractId, contractNumber, deudaTotal = 0, onClose, onChanged }: Props) {
  const { session } = useSession();
  const [view, setView] = useState<View>("list");
  const [items, setItems] = useState<InstallmentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const submittingRef = useRef(false);
  const MODAL_TYPE = "convenio";

  const [form, setForm] = useState({
    totalAmount: deudaTotal > 0 ? String(deudaTotal) : "",
    installments: "3",
    startPeriod: currentPeriod(),
    endPeriod: "",
    notes: "",
  });

  const installmentAmount = useMemo(() => {
    const total = Number(form.totalAmount.replace(/\./g, "").replace(",", "."));
    const cuotas = parseInt(form.installments, 10);
    if (!total || !cuotas || cuotas <= 0) return 0;
    return Math.ceil(total / cuotas);
  }, [form.totalAmount, form.installments]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!session) return;
    void load();
  }, [session?.userId]);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const data = await listInstallmentsByContract(session!, contractId, MODAL_TYPE);
      setItems(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al cargar convenios");
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    if (!session || submittingRef.current) return;
    const total = Number(form.totalAmount.replace(/\./g, "").replace(",", "."));
    const cuotas = parseInt(form.installments, 10);
    if (!total || total <= 0) { setError("Ingresa un monto válido"); return; }
    if (!cuotas || cuotas <= 0) { setError("Ingresa un número de cuotas válido"); return; }
    if (!form.startPeriod.match(/^\d{4}-\d{2}$/)) { setError("Período de inicio inválido (formato YYYY-MM)"); return; }

    submittingRef.current = true;
    setSaving(true);
    setError("");
    try {
      await createInstallmentAgreement(session, {
        contractId,
        type: MODAL_TYPE,
        totalAmount: total,
        installments: cuotas,
        startPeriod: form.startPeriod,
        endPeriod: form.endPeriod || undefined,
        notes: form.notes || undefined,
      });
      await load();
      onChanged?.();
      setForm({ totalAmount: "", installments: "3", startPeriod: currentPeriod(), endPeriod: "", notes: "" });
      setView("list");

    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al crear convenio");
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  }

  async function handleCancel(id: string) {
    if (!session || !confirm("¿Anular este convenio?")) return;
    setCancellingId(id);
    try {
      await updateInstallmentStatus(session, id, "cancelled");
      await load();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al anular");
    } finally {
      setCancellingId(null);
    }
  }

  async function handleComplete(id: string) {
    if (!session || !confirm("¿Marcar este convenio como completado?")) return;
    setCancellingId(id);
    try {
      await updateInstallmentStatus(session, id, "completed");
      await load();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al actualizar");
    } finally {
      setCancellingId(null);
    }
  }

  const set = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-lg shadow-2xl flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-semibold text-gray-800">
              {view === "list" ? "Convenios de pago" : "Nuevo convenio"}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">Contrato {contractNumber}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {error ? (
            <div className="mb-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          ) : null}

          {/* ─── LISTA ─── */}
          {view === "list" && (
            <div className="space-y-3">
              {loading ? (
                <div className="space-y-2">
                  {[1, 2].map((i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-gray-100" />)}
                </div>
              ) : items.length === 0 ? (
                <div className="py-10 text-center">
                  <i className="ri-file-list-3-line text-3xl text-gray-300" />
                  <p className="mt-2 text-sm text-gray-500">Sin convenios para este contrato.</p>
                </div>
              ) : (
                items.map((item) => (
                  <div
                    key={item.id}
                    className={`rounded-xl border p-4 ${item.status === "cancelled" ? "opacity-60" : ""}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-gray-800">
                            ${Number(item.totalAmount).toLocaleString("es-CL")}
                          </span>
                          <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${STATUS_CLASS[item.status] ?? STATUS_CLASS.active}`}>
                            {STATUS_LABEL[item.status] ?? item.status}
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                          {item.installments} cuotas de ${Number(item.installmentAmount).toLocaleString("es-CL")}
                          {" · "}desde {item.startPeriod}
                          {item.endPeriod ? ` hasta ${item.endPeriod}` : ""}
                        </p>
                        {item.notes ? (
                          <p className="mt-1 text-xs text-gray-400 italic">{item.notes}</p>
                        ) : null}
                        <p className="mt-1 text-[10px] text-gray-300">
                          Creado {new Date(item.createdAt).toLocaleDateString("es-CL")}
                        </p>
                      </div>
                      {item.status === "active" ? (
                        <div className="flex shrink-0 flex-col gap-1">
                          <button
                            onClick={() => handleComplete(item.id)}
                            disabled={cancellingId === item.id}
                            className="text-xs px-2 py-1 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                          >
                            Completar
                          </button>
                          <button
                            onClick={() => handleCancel(item.id)}
                            disabled={cancellingId === item.id}
                            className="text-xs px-2 py-1 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50"
                          >
                            {cancellingId === item.id ? "..." : "Anular"}
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* ─── FORMULARIO NUEVO ─── */}
          {view === "new" && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Monto total a repactar ($)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={form.totalAmount}
                  onChange={set("totalAmount")}
                  placeholder="Ej: 50000"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-teal-400 focus:outline-none"
                  required
                />
                {deudaTotal > 0 ? (
                  <button
                    type="button"
                    onClick={() => setForm((p) => ({ ...p, totalAmount: String(deudaTotal) }))}
                    className="mt-1 text-xs text-teal-600 hover:underline"
                  >
                    Usar deuda total: ${deudaTotal.toLocaleString("es-CL")}
                  </button>
                ) : null}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">N° de cuotas</label>
                  <input
                    type="number"
                    min={1}
                    max={60}
                    value={form.installments}
                    onChange={set("installments")}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-teal-400 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Valor cuota</label>
                  <div className="flex items-center rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-700">
                    ${installmentAmount > 0 ? installmentAmount.toLocaleString("es-CL") : "—"}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Período inicio (YYYY-MM)</label>
                  <input
                    type="text"
                    value={form.startPeriod}
                    onChange={set("startPeriod")}
                    placeholder="2026-07"
                    pattern="\d{4}-\d{2}"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-teal-400 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Período fin (opcional)</label>
                  <input
                    type="text"
                    value={form.endPeriod}
                    onChange={set("endPeriod")}
                    placeholder="2026-12"
                    pattern="\d{4}-\d{2}"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-teal-400 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Observaciones (opcional)</label>
                <textarea
                  value={form.notes}
                  onChange={set("notes")}
                  rows={2}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-teal-400 focus:outline-none resize-none"
                  placeholder="Ej: Cliente comprometió pago el día 5 de cada mes"
                />
              </div>

              {installmentAmount > 0 ? (
                <div className="rounded-lg bg-teal-50 border border-teal-100 px-4 py-3">
                  <p className="text-xs text-teal-700">
                    <strong>{form.installments} cuotas</strong> de{" "}
                    <strong>${installmentAmount.toLocaleString("es-CL")}</strong> desde{" "}
                    <strong>{form.startPeriod}</strong>
                    {form.endPeriod ? ` hasta ${form.endPeriod}` : ""}
                  </p>
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-6 py-4">
          {view === "list" ? (
            <>
              <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700">
                Cerrar
              </button>
              <button
                onClick={() => { setError(""); setView("new"); }}
                className="flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700"
              >
                <i className="ri-add-line" /> Nuevo convenio
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => { setError(""); setView("list"); }}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                ← Volver
              </button>
              <button
                type="button"
                disabled={saving || submittingRef.current}
                onClick={() => void handleCreate()}
                className="flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-60"
              >
                {saving ? <><i className="ri-loader-4-line animate-spin" /> Guardando...</> : <><i className="ri-save-line" /> Crear convenio</>}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
